import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ArrowLeft, Mail, Key, Lock, Check, AlertCircle, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function PasswordResetFlow({ onBack, onSuccess }) {
  const [step, setStep] = useState('email'); // 'email', 'code', 'newPassword', 'success'
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [displayedCode, setDisplayedCode] = useState(''); // For testing - shows the code
  
  // Step 1: Request reset code
  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/password-reset/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // For testing, display the code (remove in production)
        if (data.reset_code) {
          setDisplayedCode(data.reset_code);
        }
        setStep('code');
      } else {
        setError(data.detail || 'Failed to send reset code');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
    
    setLoading(false);
  };
  
  // Step 2: Verify code and set new password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/password-reset/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          reset_code: resetCode,
          new_password: newPassword
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setStep('success');
      } else {
        setError(data.detail || 'Failed to reset password');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
    
    setLoading(false);
  };
  
  return (
    <Card className="p-6 bg-card border-border max-w-md mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="p-1"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h2 className="font-display font-bold text-xl text-foreground">Reset Password</h2>
          <p className="text-sm text-muted-foreground">
            {step === 'email' && "Enter your email to receive a reset code"}
            {step === 'code' && "Enter the code and your new password"}
            {step === 'success' && "Password reset complete!"}
          </p>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-destructive" />
          <span className="text-sm text-destructive">{error}</span>
        </div>
      )}
      
      {/* Step 1: Email */}
      {step === 'email' && (
        <form onSubmit={handleRequestReset} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="pl-10"
                required
              />
            </div>
          </div>
          
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Send Reset Code
              </>
            )}
          </Button>
        </form>
      )}
      
      {/* Step 2: Code + New Password */}
      {step === 'code' && (
        <form onSubmit={handleResetPassword} className="space-y-4">
          {/* Display code for testing */}
          {displayedCode && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-xs text-blue-400 mb-1">Your reset code (for testing):</p>
              <p className="font-mono text-2xl text-blue-400 font-bold tracking-widest text-center">
                {displayedCode}
              </p>
            </div>
          )}
          
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Reset Code</label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                placeholder="Enter 6-digit code"
                className="pl-10 text-center tracking-widest font-mono text-lg"
                maxLength={6}
                required
              />
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">New Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password (min 6 chars)"
                className="pl-10"
                minLength={6}
                required
              />
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Confirm Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="pl-10"
                required
              />
            </div>
          </div>
          
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Resetting...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Reset Password
              </>
            )}
          </Button>
          
          <Button
            type="button"
            variant="ghost"
            className="w-full text-muted-foreground"
            onClick={() => setStep('email')}
          >
            Didn't receive code? Try again
          </Button>
        </form>
      )}
      
      {/* Step 3: Success */}
      {step === 'success' && (
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center">
            <Check className="w-8 h-8 text-green-500" />
          </div>
          
          <div>
            <h3 className="font-display font-bold text-lg text-foreground">Password Reset!</h3>
            <p className="text-sm text-muted-foreground">
              Your password has been successfully changed.
            </p>
          </div>
          
          <Button onClick={onSuccess || onBack} className="w-full">
            Back to Login
          </Button>
        </div>
      )}
    </Card>
  );
}
